// =============================================================================
// GITHUB INTEGRATION CLASS (CLOUDFLARE FUNCTIONS ONLY)
// =============================================================================

class GitHubBlogPublisher {
    constructor(config) {
        // Remove direct GitHub API usage - use Cloudflare Functions instead
        this.baseUrl = window.location.origin;
        this.repoOwner = config.repoOwner;
        this.repoName = config.repoName;
        this.branch = config.branch || "main";
    }

    // Use Cloudflare Function to publish blog
    async createMDXFile(filename, content, commitMessage) {
        try {
            const response = await fetch(`${this.baseUrl}/api/publish-blog`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    filename: filename,
                    content: content,
                    message: commitMessage,
                    path: `docs/pages/grants/${filename}`,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`API error: ${response.status} - ${errorData.error || errorData.message}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error publishing blog:", error);
            throw error;
        }
    }

    // Use Cloudflare Function to check files
    async checkFileExists(filename) {
        try {
            const response = await fetch(`${this.baseUrl}/api/check-file`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    filename: filename,
                    path: `docs/pages/grants/${filename}`,
                }),
            });

            if (!response.ok) return false;

            const result = await response.json();
            return result.exists;
        } catch (error) {
            console.error("Error checking file:", error);
            return false;
        }
    }

    // Use Cloudflare Function to test connection
    async getRepositoryInfo() {
        try {
            const response = await fetch(`${this.baseUrl}/api/repo-info`);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            return result.repository; // Return the nested repository data
        } catch (error) {
            console.error("Error getting repository info:", error);
            throw error;
        }
    }

    // Add this to your GitHubBlogPublisher class
    async deleteBlogFile(filename, reason = "Rejected") {
        try {
            const response = await fetch(`${this.baseUrl}/api/delete-blog`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    filename: filename,
                    reason: reason,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`API error: ${response.status} - ${errorData.error || errorData.message}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error deleting blog:", error);
            throw error;
        }
    }
}

// =============================================================================
// DRAFT READER CLASS (NO GITHUB SECRETS)
// =============================================================================

class DraftReader {
    constructor() {
        this.baseUrl = "blog_drafts/";
        this.drafts = [];
        this.selectedDraft = null;

        // Simple configuration - no secrets in frontend
        this.githubConfig = {
            repoOwner: import.meta.env.VITE_GITHUB_REPO_OWNER || "voltade",
            repoName: import.meta.env.VITE_GITHUB_REPO_NAME || "voltade-blog",
            branch: import.meta.env.VITE_GITHUB_BRANCH || "main",
        };

        // Load saved configuration (non-sensitive data only)
        this.loadSavedConfig();
    }

    // Keep the rest of your existing methods...
    async loadDrafts() {
        try {
            const response = await fetch(`${this.baseUrl}manifest.json`);
            const manifest = await response.json();

            let fileList = [];
            let fileDetails = {};

            if (manifest.file_details) {
                fileList = manifest.files;
                manifest.file_details.forEach((detail) => {
                    fileDetails[detail.filename] = detail;
                });
            } else {
                fileList = manifest.files;
            }

            this.drafts = await Promise.all(
                fileList.map(async (filename) => {
                    const content = await this.loadDraftContent(filename);
                    const frontmatter = this.parseFrontmatter(content);
                    const contentWithoutFrontmatter = this.removeFrontmatter(content);

                    const metadata = fileDetails[filename] || {};

                    return {
                        id: filename,
                        filename: filename,
                        title: metadata.title || frontmatter.title || filename,
                        created: metadata.created || frontmatter.date || new Date().toISOString(),
                        category: metadata.category || frontmatter.category || "Grants",
                        readTime: metadata.readTime || frontmatter.readTime || "5 min read",
                        author: metadata.author || frontmatter.author || "Grant Intelligence Team",
                        tags: metadata.tags || frontmatter.tags || [],
                        description: metadata.description || frontmatter.description || "",
                        image: metadata.image || frontmatter.image || "/images/blog/grants/default-grant.jpg",
                        status: this.getDraftStatus(filename),
                        content: content,
                        cleanContent: contentWithoutFrontmatter,
                        frontmatter: frontmatter,
                        size: metadata.size || 0,
                        modified: metadata.modified || metadata.created || new Date().toISOString(),
                    };
                })
            );

            this.drafts.sort((a, b) => new Date(b.created) - new Date(a.created));

            console.log(`✅ Loaded ${this.drafts.length} drafts from manifest`);
            return this.drafts;
        } catch (error) {
            console.error("Error loading drafts:", error);
            console.log("📝 Using mock data as fallback");
            return this.getMockDrafts();
        }
    }

    async loadDraftContent(filename) {
        try {
            const response = await fetch(this.baseUrl + filename);
            return await response.text();
        } catch (error) {
            console.error(`Error loading ${filename}:`, error);
            return this.getMockContent(filename);
        }
    }

    parseFrontmatter(content) {
        const lines = content.split("\n");
        const frontmatter = {};

        if (lines[0] === "---") {
            for (let i = 1; i < lines.length; i++) {
                if (lines[i] === "---") {
                    break;
                }

                const [key, ...valueParts] = lines[i].split(":");
                if (key && valueParts.length > 0) {
                    let value = valueParts.join(":").trim();

                    if (value.startsWith('"') && value.endsWith('"')) {
                        value = value.slice(1, -1);
                    }

                    if (key === "tags" && value.startsWith("[")) {
                        try {
                            value = JSON.parse(value.replace(/"/g, '"'));
                        } catch (e) {
                            value = [];
                        }
                    }

                    frontmatter[key] = value;
                }
            }
        }

        return frontmatter;
    }

    removeFrontmatter(content) {
        const lines = content.split("\n");
        if (lines[0] === "---") {
            let endIndex = -1;
            for (let i = 1; i < lines.length; i++) {
                if (lines[i] === "---") {
                    endIndex = i;
                    break;
                }
            }
            if (endIndex > 0) {
                return lines.slice(endIndex + 1).join("\n");
            }
        }
        return content;
    }

    getDraftStatus(filename) {
        const savedStatus = JSON.parse(localStorage.getItem("draftStatus") || "{}");
        return savedStatus[filename] || "draft";
    }

    updateDraftStatus(filename, status) {
        const savedStatus = JSON.parse(localStorage.getItem("draftStatus") || "{}");
        savedStatus[filename] = status;
        localStorage.setItem("draftStatus", JSON.stringify(savedStatus));
    }

    generateFilename(draft) {
        const title = draft.title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            .substring(0, 50)
            .replace(/-$/, "");

        return `${title}.mdx`;
    }

    generateMDXContent(draft) {
        return draft.content;
    }

    renderBlogLayout(draft) {
        const { frontmatter, cleanContent } = draft;
        const {
            title,
            description,
            date,
            category,
            readTime,
            author = "Grant Intelligence Team",
            image,
            tags = [],
        } = frontmatter || {};

        const cleanedContent = this.cleanMarkdownContent(cleanContent);
        const htmlContent = marked.parse(cleanedContent);

        return `
            <div class="blog-layout">
                <article class="blog-content vocs_Content">
                    ${
                        title || image
                            ? `
                        <header class="blog-header vocs_Header">
                            ${title ? `<h1 class="blog-title vocs_H1 vocs_Heading">${title}</h1>` : ""}
                            ${description ? `<p class="blog-description vocs_Paragraph">${description}</p>` : ""}
                            ${
                                image
                                    ? `
                                <div class="blog-hero-image">
                                    <img src="${image}" alt="${title || "Blog post image"}" />
                                </div>
                            `
                                    : ""
                            }
                            
                            <div class="author-section">
                                <div class="author-info">
                                    <div class="author-avatar">
                                        <img src="/images/assets/Favicon.png" alt="${author}" />
                                    </div>
                                    <div class="author-details">
                                        <span class="author-name vocs_Paragraph">${author}</span>
                                        <div class="post-meta">
                                            ${date ? `<span class="post-date vocs_Paragraph">${date}</span>` : ""}
                                            ${
                                                date && readTime
                                                    ? `<span class="meta-divider vocs_Paragraph">•</span>`
                                                    : ""
                                            }
                                            ${
                                                readTime
                                                    ? `<span class="read-time vocs_Paragraph">${readTime}</span>`
                                                    : ""
                                            }
                                            ${
                                                (date || readTime) && category
                                                    ? `<span class="meta-divider vocs_Paragraph">•</span>`
                                                    : ""
                                            }
                                            ${
                                                category
                                                    ? `<span class="badge badge-primary vocs_Paragraph">${category}</span>`
                                                    : ""
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </header>
                    `
                            : ""
                    }
                    
                    <div class="blog-main">
                        ${htmlContent}
                    </div>
                    
                    ${
                        tags.length > 0
                            ? `
                        <div class="blog-tags">
                            ${tags.map((tag) => `<span class="blog-tag vocs_Paragraph">${tag}</span>`).join("")}
                        </div>
                    `
                            : ""
                    }
                    
                    <div class="related-articles">
                        <h2 class="vocs_H2 vocs_Heading">Related Articles</h2>
                        <ul>
                            <li><a href="/whatsapp/business-guide">WhatsApp Business Integration Guide</a></li>
                            <li><a href="/crm/automation-strategies">CRM Automation Best Practices</a></li>
                            <li><a href="/sales-marketing/lead-management">Lead Management Optimization</a></li>
                            <li><a href="/product-support/troubleshooting">Technical Support Resources</a></li>
                        </ul>
                    </div>
                    
                    <h2 class="vocs_H2 vocs_Heading">Connect With Us !</h2>
                    <div class="newsletter-form">
                        <p>Subscribe to our newsletter for updates!</p>
                        <form>
                            <input type="email" placeholder="Enter your email" required>
                            <button type="submit">Subscribe</button>
                        </form>
                    </div>
                </article>
            </div>
        `;
    }

    cleanMarkdownContent(content) {
        return content
            .replace(/import\s+.*?from\s+['"].*?['"];?\s*/g, "")
            .replace(/export\s+.*?=\s*{[\s\S]*?};?\s*/g, "")
            .replace(/<BlogLayout[^>]*>[\s\S]*?<\/BlogLayout>/g, (match) => {
                const innerContent = match.replace(/<\/?BlogLayout[^>]*>/g, "");
                return innerContent;
            })
            .replace(/export\s+default\s+.*?;?\s*/g, "")
            .trim();
    }

    getMockDrafts() {
        return [
            {
                id: "singapore_sme_grants_comprehensive_20250716_154618",
                filename: "singapore_sme_grants_comprehensive_20250716_154618.md",
                title: "Complete Guide to Singapore SME Grants 2025: 18 Essential Funding Opportunities",
                created: "2025-07-16T15:46:18",
                category: "Business Funding",
                readTime: "8 min read",
                author: "Grant Intelligence Team",
                tags: ["SME Grants", "Singapore Business", "Government Funding", "Entrepreneurship"],
                status: this.getDraftStatus("singapore_sme_grants_comprehensive_20250716_154618.md"),
                content: this.getMockContent("comprehensive"),
                cleanContent: this.removeFrontmatter(this.getMockContent("comprehensive")),
                frontmatter: this.parseFrontmatter(this.getMockContent("comprehensive")),
            },
        ];
    }

    getMockContent(type) {
        if (type === "comprehensive") {
            return `---
title: "Complete Guide to Singapore SME Grants 2025: 18 Essential Funding Opportunities"
description: "Comprehensive guide covering all Singapore SME grants available in 2025"
date: "July 16, 2025"
category: "Business Funding"
readTime: "8 min read"
author: "Grant Intelligence Team"
image: "/images/blog/singapore-sme-grants.jpg"
tags: ["SME Grants", "Singapore Business", "Government Funding"]
---

# Complete Guide to Singapore SME Grants 2025

Mock content here...`;
        }
        return "Mock content not available";
    }

    // FIXED: Load saved configuration (no token references)
    loadSavedConfig() {
        const savedConfig = localStorage.getItem("githubConfig");
        if (savedConfig) {
            const config = JSON.parse(savedConfig);
            this.githubConfig = {
                ...this.githubConfig,
                ...config,
                // Removed githubToken line completely
            };
        }
    }

    // Auto-save GitHub config (non-sensitive only)
    autoSaveConfig() {
        const config = {
            repoOwner: this.githubConfig.repoOwner,
            repoName: this.githubConfig.repoName,
            branch: this.githubConfig.branch,
        };
        localStorage.setItem("githubConfig", JSON.stringify(config));
    }

    async exportApprovedDrafts() {
        const approvedDrafts = this.drafts.filter((d) => d.status === "approved");
        return approvedDrafts.map((draft) => ({
            filename: draft.filename,
            title: draft.title,
            content: draft.content,
            frontmatter: draft.frontmatter,
        }));
    }

    async generateManifest() {
        const manifest = {
            generated: new Date().toISOString(),
            files: this.drafts.map((d) => d.filename),
            summary: {
                total: this.drafts.length,
                approved: this.drafts.filter((d) => d.status === "approved").length,
                pending: this.drafts.filter((d) => d.status === "draft").length,
                rejected: this.drafts.filter((d) => d.status === "rejected").length,
            },
        };

        return JSON.stringify(manifest, null, 2);
    }
}

// =============================================================================
// DASHBOARD FUNCTIONALITY
// =============================================================================

const draftReader = new DraftReader();
let selectedDraft = null;

async function loadDrafts() {
    try {
        const draftsContainer = document.getElementById("drafts-container");
        draftsContainer.innerHTML = '<div class="loading"><div class="spinner"></div>Loading drafts...</div>';

        const drafts = await draftReader.loadDrafts();

        document.getElementById("total-drafts").textContent = drafts.length;
        document.getElementById("pending-drafts").textContent = drafts.filter((d) => d.status === "draft").length;
        document.getElementById("approved-drafts").textContent = drafts.filter((d) => d.status === "approved").length;

        draftsContainer.innerHTML = drafts
            .map(
                (draft) => `
            <div class="draft-item" onclick="selectDraft('${draft.id}')">
                <div class="draft-title">${draft.title}</div>
                <div class="draft-description">${
                    draft.description ? draft.description.substring(0, 100) + "..." : ""
                }</div>
                <div class="draft-meta">
                    <span class="draft-date">${draft.created.split("T")[0]}</span>
                    <span class="draft-category">${draft.category}</span>
                    <span class="draft-read-time">${draft.readTime}</span>
                    <span class="draft-status draft-status-${draft.status}">${draft.status}</span>
                </div>
                ${draft.size ? `<div class="draft-size">${Math.round(draft.size / 1024)}KB</div>` : ""}
            </div>
        `
            )
            .join("");
    } catch (error) {
        console.error("Error loading drafts:", error);
        document.getElementById("drafts-container").innerHTML = '<div class="error">Error loading drafts</div>';
    }
}

function selectDraft(draftId) {
    selectedDraft = draftReader.drafts.find((d) => d.id === draftId);

    if (!selectedDraft) {
        console.error("Draft not found:", draftId);
        return;
    }

    // Update visual selection
    document.querySelectorAll(".draft-item").forEach((item) => item.classList.remove("selected"));
    event.target.closest(".draft-item").classList.add("selected");

    // Update preview content
    document.getElementById("preview-title").textContent = selectedDraft.title;
    document.getElementById("preview-content").innerHTML = draftReader.renderBlogLayout(selectedDraft);

    // FIXED: Update button based on draft status
    updateActionButtons(selectedDraft.status);
}

function updateActionButtons(status) {
    // Get the buttons using proper selectors
    const approveBtn =
        document.querySelector("#preview-actions .btn-success") ||
        document.querySelector(".preview-actions .btn-success");
    const rejectBtn =
        document.querySelector("#preview-actions .btn-danger") ||
        document.querySelector(".preview-actions .btn-danger");
    const downloadBtn =
        document.querySelector("#preview-actions .btn-secondary") ||
        document.querySelector(".preview-actions .btn-secondary");
    const editBtn =
        document.querySelector("#preview-actions .btn-primary") ||
        document.querySelector(".preview-actions .btn-primary");

    // Reset all buttons to default state
    if (approveBtn) {
        approveBtn.textContent = "✅ Approve";
        approveBtn.disabled = false;
        approveBtn.style.display = "";
    }

    if (rejectBtn) {
        rejectBtn.textContent = "❌ Reject";
        rejectBtn.disabled = false;
        rejectBtn.style.display = "";
    }

    // Update buttons based on status
    switch (status) {
        case "published":
            if (rejectBtn) {
                rejectBtn.textContent = "🗑️ Delete";
                rejectBtn.title = "Permanently delete this published post";
            }
            if (approveBtn) {
                approveBtn.style.display = "none"; // Hide approve button
            }
            break;

        case "approved":
            if (approveBtn) {
                approveBtn.textContent = "✅ Approved";
                approveBtn.disabled = true;
            }
            if (rejectBtn) {
                rejectBtn.textContent = "↩️ Unapprove";
                rejectBtn.title = "Move back to draft status";
            }
            break;

        case "rejected":
            if (rejectBtn) {
                rejectBtn.textContent = "❌ Rejected";
                rejectBtn.disabled = true;
            }
            if (approveBtn) {
                approveBtn.textContent = "🔄 Re-approve";
                approveBtn.title = "Move back to pending approval";
            }
            break;

        case "draft":
        default:
            // Default state - buttons already set above
            break;
    }

    // Add status indicator
    addStatusIndicator(status);
}

function addStatusIndicator(status) {
    // Remove existing status indicator
    const existingIndicator = document.querySelector(".draft-status-indicator");
    if (existingIndicator) {
        existingIndicator.remove();
    }

    // Add new status indicator
    const previewHeader = document.querySelector(".preview-header");
    if (previewHeader) {
        const statusColors = {
            draft: "#ffc107", // Yellow
            approved: "#28a745", // Green
            rejected: "#dc3545", // Red
            published: "#17a2b8", // Blue
        };

        const statusIndicator = document.createElement("div");
        statusIndicator.className = "draft-status-indicator";
        statusIndicator.innerHTML = `
            <span class="status-badge" style="
                background: ${statusColors[status] || "#6c757d"}; 
                color: white; 
                padding: 0.25rem 0.75rem; 
                border-radius: 12px; 
                font-size: 0.8rem; 
                font-weight: 500;
                text-transform: capitalize;
            ">
                ${status}
            </span>
        `;

        // Insert after the title
        const title = previewHeader.querySelector("#preview-title");
        if (title) {
            title.appendChild(statusIndicator);
        }
    }
}

// =============================================================================
// FIXED APPROVAL FUNCTION (NO TOKEN CHECKS)
// =============================================================================

async function approveDraft() {
    if (!selectedDraft) {
        alert("❌ Please select a draft first.");
        return;
    }

    // Show loading state
    const approveBtn = document.querySelector(".btn-success");
    if (approveBtn) {
        const originalText = approveBtn.textContent;
        approveBtn.textContent = "🚀 Publishing...";
        approveBtn.disabled = true;
        approveBtn.classList.add("btn-publishing");

        try {
            await approveDraftAutomated();
        } finally {
            approveBtn.textContent = originalText;
            approveBtn.disabled = false;
            approveBtn.classList.remove("btn-publishing");
        }
    } else {
        // Fallback to basic approval
        draftReader.updateDraftStatus(selectedDraft.filename, "approved");
        selectedDraft.status = "approved";
        loadDrafts();
    }
}

async function approveDraftAutomated() {
    if (!selectedDraft) return;

    try {
        // Initialize GitHub publisher
        const publisher = new GitHubBlogPublisher(draftReader.githubConfig);

        // Generate filename and content
        const filename = draftReader.generateFilename(selectedDraft);
        const mdxContent = draftReader.generateMDXContent(selectedDraft);
        const commitMessage = `📝 Add new blog post: ${selectedDraft.title}`;

        // Check if file already exists
        const fileExists = await publisher.checkFileExists(filename);
        if (fileExists) {
            throw new Error(`File ${filename} already exists in the repository`);
        }

        // Create MDX file via Cloudflare Function
        const result = await publisher.createMDXFile(filename, mdxContent, commitMessage);

        if (result.success) {
            // Update local status
            draftReader.updateDraftStatus(selectedDraft.filename, "published");
            selectedDraft.status = "published";

            // Store publication info (simplified structure)
            const publicationInfo = {
                filename: filename,
                githubPath: `docs/pages/grants/${filename}`,
                publishedAt: new Date().toISOString(),
                blogUrl: `/grants/${filename.replace(".mdx", "")}`,
                workflowTriggered: true,
            };

            // Store in localStorage for tracking
            const publishedPosts = JSON.parse(localStorage.getItem("publishedPosts") || "[]");
            publishedPosts.push(publicationInfo);
            localStorage.setItem("publishedPosts", JSON.stringify(publishedPosts));

            // Success feedback
            showSuccessModal(publicationInfo);
            loadDrafts();
        } else {
            throw new Error(result.error || "Failed to trigger blog publication");
        }
    } catch (error) {
        console.error("Publication error:", error);
        alert(`❌ Publication failed: ${error.message}`);
    }
}

function showSuccessModal(publicationInfo) {
    const modalHTML = `
        <div id="success-modal" class="modal-overlay" onclick="closeSuccessModal()">
            <div class="modal-content success-modal" onclick="event.stopPropagation()">
                <div class="modal-header">
                    <h2>🎉 Blog Post Publication Triggered!</h2>
                    <button onclick="closeSuccessModal()" class="close-btn">&times;</button>
                </div>
                
                <div class="modal-body">
                    <div class="success-info">
                        <h3>📄 Publication Details</h3>
                        <ul>
                            <li><strong>File:</strong> ${publicationInfo.filename}</li>
                            <li><strong>Path:</strong> ${publicationInfo.githubPath}</li>
                            <li><strong>Triggered:</strong> ${new Date(
                                publicationInfo.publishedAt
                            ).toLocaleString()}</li>
                        </ul>
                        
                        <h3>🚀 What Happens Next</h3>
                        <ol>
                            <li>GitHub Actions workflow is now running</li>
                            <li>Blog post will be created in your repository</li>
                            <li>Blog index will be automatically updated</li>
                            <li>Site will be deployed automatically</li>
                            <li>Your post will be live at: <code>${publicationInfo.blogUrl}</code></li>
                        </ol>
                        
                        <div class="deployment-status">
                            <p>⏳ Deployment typically takes 2-3 minutes...</p>
                            <a href="https://github.com/${draftReader.githubConfig.repoOwner}/${
        draftReader.githubConfig.repoName
    }/actions" target="_blank">
                                📊 View Deployment Status
                            </a>
                        </div>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button onclick="openBlogPost('${publicationInfo.blogUrl}')" class="view-btn">👁️ View Post</button>
                    <button onclick="closeSuccessModal()" class="close-btn">Close</button>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);
}

function closeSuccessModal() {
    const modal = document.getElementById("success-modal");
    if (modal) modal.remove();
}

function openBlogPost(url) {
    setTimeout(() => {
        window.open(url, "_blank");
    }, 2000);
}

// =============================================================================
// OTHER DASHBOARD FUNCTIONS
// =============================================================================

// Updated reject function
async function rejectDraft() {
    if (!selectedDraft) {
        alert("❌ Please select a draft first.");
        return;
    }

    const reason = prompt("📝 Reason for rejection (optional):", "Quality review failed") || "Rejected";

    if (
        !confirm(
            `🗑️ Are you sure you want to PERMANENTLY DELETE this draft?\n\nFile: ${selectedDraft.filename}\nReason: ${reason}\n\n⚠️ This action cannot be undone!`
        )
    ) {
        return;
    }

    // Show loading state
    const rejectBtn = document.querySelector(".btn-danger");
    if (rejectBtn) {
        const originalText = rejectBtn.textContent;
        rejectBtn.textContent = "🗑️ Deleting...";
        rejectBtn.disabled = true;

        try {
            // Option A: Delete via Cloudflare Function (if you created the delete-blog API)
            if (typeof GitHubBlogPublisher !== "undefined") {
                const publisher = new GitHubBlogPublisher(draftReader.githubConfig);
                const result = await publisher.deleteBlogFile(selectedDraft.filename, reason);

                if (result.success) {
                    // Update local status
                    draftReader.updateDraftStatus(selectedDraft.filename, "deleted");
                    selectedDraft.status = "deleted";

                    // Remove from local drafts array
                    const index = draftReader.drafts.findIndex((d) => d.id === selectedDraft.id);
                    if (index > -1) {
                        draftReader.drafts.splice(index, 1);
                    }

                    // Clear selection
                    const draftToDelete = { filename: selectedDraft.filename, ...selectedDraft };
                    selectedDraft = null; // Clear selection after storing data
                    document.getElementById("preview-title").textContent = "Select a draft to preview";
                    document.getElementById("preview-content").innerHTML = "<p>No draft selected</p>";

                    // Reload drafts
                    await loadDrafts();

                    showNotification(`✅ Draft "${draftToDelete.filename}" deleted successfully`, "success");
                } else {
                    throw new Error(result.error || "Failed to delete draft");
                }
            } else {
                // Option B: Local deletion only (fallback)
                draftReader.updateDraftStatus(selectedDraft.filename, "rejected");
                selectedDraft.status = "rejected";
                loadDrafts();

                showNotification(`⚠️ Draft marked as rejected locally. File still exists in repository.`, "warning");
            }
        } catch (error) {
            console.error("Deletion error:", error);
            alert(`❌ Failed to delete draft: ${error.message}`);
        } finally {
            rejectBtn.textContent = originalText;
            rejectBtn.disabled = false;
        }
    }
}

function downloadDraft() {
    if (!selectedDraft) {
        alert("❌ Please select a draft first.");
        return;
    }

    const blob = new Blob([selectedDraft.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = selectedDraft.filename;
    a.click();
    URL.revokeObjectURL(url);
}

function editDraft() {
    if (!selectedDraft) {
        alert("❌ Please select a draft first.");
        return;
    }
    alert("Edit functionality would open an editor here");
}

// =============================================================================
// SIMPLIFIED SETTINGS MENU (NO TOKEN CONFIGURATION)
// =============================================================================

function showSettingsMenu() {
    const menuHTML = `
        <div id="settings-menu" class="settings-dropdown">
            <button onclick="testGitHubConnection()" class="settings-item">
                🔍 Test GitHub Connection
            </button>
            <button onclick="viewPublishedPosts()" class="settings-item">
                📊 View Published Posts
            </button>
            <button onclick="clearLocalData()" class="settings-item">
                🗑️ Clear Local Data
            </button>
            <button onclick="showRepoConfig()" class="settings-item">
                ⚙️ Repository Settings
            </button>
        </div>
    `;

    const existingMenu = document.getElementById("settings-menu");
    if (existingMenu) {
        existingMenu.remove();
        return;
    }

    document.body.insertAdjacentHTML("beforeend", menuHTML);

    document.addEventListener("click", function closeMenu(e) {
        if (!e.target.closest("#settings-menu") && !e.target.closest(".settings-btn")) {
            const menu = document.getElementById("settings-menu");
            if (menu) menu.remove();
            document.removeEventListener("click", closeMenu);
        }
    });
}

async function testGitHubConnection() {
    const settingsMenu = document.getElementById("settings-menu");
    if (settingsMenu) settingsMenu.remove();

    try {
        const publisher = new GitHubBlogPublisher(draftReader.githubConfig);
        const repoData = await publisher.getRepositoryInfo();

        alert(
            `✅ GitHub connection successful!\n\nRepository: ${repoData.full_name}\nDescription: ${
                repoData.description || "No description"
            }\nDefault branch: ${repoData.default_branch}\nPrivate: ${repoData.private ? "Yes" : "No"}`
        );
    } catch (error) {
        alert(`❌ GitHub connection failed: ${error.message}`);
    }
}

function showRepoConfig() {
    const settingsMenu = document.getElementById("settings-menu");
    if (settingsMenu) settingsMenu.remove();

    const modalHTML = `
        <div id="repo-config-modal" class="modal-overlay" onclick="closeRepoConfig()">
            <div class="modal-content" onclick="event.stopPropagation()">
                <div class="modal-header">
                    <h2>⚙️ Repository Configuration</h2>
                    <button onclick="closeRepoConfig()" class="close-btn">&times;</button>
                </div>
                
                <div class="modal-body">
                    <p>Configure which repository to publish blogs to:</p>
                    
                    <div class="form-group">
                        <label for="repo-owner">GitHub Username:</label>
                        <input type="text" id="repo-owner" value="${draftReader.githubConfig.repoOwner}">
                    </div>
                    <div class="form-group">
                        <label for="repo-name">Repository Name:</label>
                        <input type="text" id="repo-name" value="${draftReader.githubConfig.repoName}">
                    </div>
                    <div class="form-group">
                        <label for="repo-branch">Branch:</label>
                        <input type="text" id="repo-branch" value="${draftReader.githubConfig.branch}">
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button onclick="saveRepoConfig()" class="save-btn">💾 Save Configuration</button>
                    <button onclick="closeRepoConfig()" class="close-btn">Cancel</button>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);
}

function saveRepoConfig() {
    const repoOwner = document.getElementById("repo-owner").value;
    const repoName = document.getElementById("repo-name").value;
    const branch = document.getElementById("repo-branch").value;

    if (!repoOwner || !repoName) {
        alert("❌ Please enter repository owner and name");
        return;
    }

    // Update draftReader config
    draftReader.githubConfig.repoOwner = repoOwner;
    draftReader.githubConfig.repoName = repoName;
    draftReader.githubConfig.branch = branch;

    // Save config
    draftReader.autoSaveConfig();

    closeRepoConfig();
    alert("✅ Repository configuration saved!");
}

function closeRepoConfig() {
    const modal = document.getElementById("repo-config-modal");
    if (modal) modal.remove();
}

function viewPublishedPosts() {
    const settingsMenu = document.getElementById("settings-menu");
    if (settingsMenu) settingsMenu.remove();

    const publishedPosts = JSON.parse(localStorage.getItem("publishedPosts") || "[]");

    if (publishedPosts.length === 0) {
        alert("📝 No published posts found.");
        return;
    }

    const postsHTML = publishedPosts
        .map(
            (post) => `
        <div class="published-post">
            <h4>${post.filename}</h4>
            <p><strong>Published:</strong> ${new Date(post.publishedAt).toLocaleString()}</p>
            <p><strong>Path:</strong> ${post.githubPath}</p>
            <p><strong>Status:</strong> ${post.workflowTriggered ? "✅ Workflow Triggered" : "❓ Unknown"}</p>
            <p><strong>URL:</strong> <a href="${post.blogUrl}" target="_blank">${post.blogUrl}</a></p>
        </div>
    `
        )
        .join("");

    const modalHTML = `
        <div id="published-posts-modal" class="modal-overlay" onclick="closePublishedPostsModal()">
            <div class="modal-content" onclick="event.stopPropagation()">
                <div class="modal-header">
                    <h2>📊 Published Posts (${publishedPosts.length})</h2>
                    <button onclick="closePublishedPostsModal()" class="close-btn">&times;</button>
                </div>
                
                <div class="modal-body">
                    ${postsHTML}
                </div>
                
                <div class="modal-footer">
                    <button onclick="closePublishedPostsModal()" class="close-btn">Close</button>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);
}

function closePublishedPostsModal() {
    const modal = document.getElementById("published-posts-modal");
    if (modal) modal.remove();
}

function clearLocalData() {
    const settingsMenu = document.getElementById("settings-menu");
    if (settingsMenu) settingsMenu.remove();

    if (
        confirm(
            "⚠️ Are you sure you want to clear all local data? This will remove draft statuses and published post history."
        )
    ) {
        localStorage.removeItem("draftStatus");
        localStorage.removeItem("publishedPosts");
        localStorage.removeItem("githubConfig");

        alert("✅ Local data cleared successfully!");

        // Reset GitHub config to defaults
        draftReader.githubConfig = {
            repoOwner: import.meta.env.VITE_GITHUB_REPO_OWNER || "voltade",
            repoName: import.meta.env.VITE_GITHUB_REPO_NAME || "voltade-blog",
            branch: import.meta.env.VITE_GITHUB_BRANCH || "main",
        };

        loadDrafts();
    }
}

// =============================================================================
// INITIALIZE DASHBOARD
// =============================================================================

document.addEventListener("DOMContentLoaded", async function () {
    // Load drafts immediately
    loadDrafts();

    // Add settings button if it doesn't exist
    if (!document.querySelector(".settings-btn")) {
        const settingsBtn = document.createElement("button");
        settingsBtn.className = "settings-btn";
        settingsBtn.innerHTML = "⚙️ Settings";
        settingsBtn.onclick = showSettingsMenu;
        document.body.appendChild(settingsBtn);
    }

    // Load saved configuration
    draftReader.loadSavedConfig();

    console.log("✅ Blog publishing system initialized");
    console.log(`📁 Target Repository: ${draftReader.githubConfig.repoOwner}/${draftReader.githubConfig.repoName}`);
    console.log("🔐 Using Cloudflare Functions for secure API access");
});

// =============================================================================
// KEYBOARD SHORTCUTS
// =============================================================================

document.addEventListener("keydown", function (e) {
    // Ctrl/Cmd + Enter to approve draft
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        approveDraft();
    }

    // Ctrl/Cmd + R to refresh drafts
    if ((e.ctrlKey || e.metaKey) && e.key === "r") {
        e.preventDefault();
        loadDrafts();
    }

    // Escape to close modals
    if (e.key === "Escape") {
        const modals = document.querySelectorAll(".modal-overlay");
        modals.forEach((modal) => modal.remove());

        const settingsMenu = document.getElementById("settings-menu");
        if (settingsMenu) settingsMenu.remove();
    }
});

// =============================================================================
// NOTIFICATION SYSTEM
// =============================================================================

function showNotification(message, type = "info") {
    const notification = document.createElement("div");
    notification.className = `notification notification-${type}`;
    notification.textContent = message;

    document.body.appendChild(notification);

    setTimeout(() => {
        if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
        }
    }, 3000);
}

// =============================================================================
// ERROR HANDLING
// =============================================================================

window.addEventListener("error", function (e) {
    console.error("Global error:", e.error);
    showNotification("An error occurred. Please check the console for details.", "error");
});

window.addEventListener("unhandledrejection", function (e) {
    console.error("Unhandled promise rejection:", e.reason);
    showNotification("An async error occurred. Please check the console for details.", "error");
});

// =============================================================================
// EXPORT FOR USE IN MAIN APPLICATION
// =============================================================================

window.DraftReader = DraftReader;
window.GitHubBlogPublisher = GitHubBlogPublisher;

// Export main functions for external use
window.loadDrafts = loadDrafts;
window.selectDraft = selectDraft;
window.approveDraft = approveDraft;
window.rejectDraft = rejectDraft;
window.downloadDraft = downloadDraft;
window.editDraft = editDraft;
window.showSettingsMenu = showSettingsMenu;
window.closeSuccessModal = closeSuccessModal;
window.openBlogPost = openBlogPost;

// =============================================================================
// DEVELOPMENT HELPERS
// =============================================================================

if (typeof window !== "undefined") {
    window.dev = {
        getDrafts: () => draftReader.drafts,
        getSelectedDraft: () => selectedDraft,
        getGitHubConfig: () => draftReader.githubConfig,
        clearStorage: () => {
            localStorage.clear();
            console.log("All localStorage cleared");
        },
        testGitHub: testGitHubConnection,
        exportDrafts: () => draftReader.exportApprovedDrafts(),
        generateManifest: () => draftReader.generateManifest(),
    };

    console.log("🔧 Development helpers available at window.dev");
}
